import FeatureLayer from '@arcgis/core/layers/FeatureLayer';

import { WGS84_SPATIAL_REFERENCE } from '../LayersTab/utils/spatialReference';

export type ArcgisLocationLink = {
  external_layer_id: string;
  object_id: number;
};

export type ArcgisFeatureChoice = {
  objectId: number;
  label: string;
  longitude: number;
  latitude: number;
};

export function parseArcgisLocationLink(
  value: unknown
): ArcgisLocationLink | null {
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  const externalLayerId = record.external_layer_id ?? record.externalLayerId;
  const objectId = record.object_id ?? record.objectId;
  if (typeof externalLayerId !== 'string' || !externalLayerId) return null;
  const parsed = typeof objectId === 'number' ? objectId : Number(objectId);
  if (!Number.isFinite(parsed)) return null;
  return { external_layer_id: externalLayerId, object_id: parsed };
}

export function isQueryableFeatureService(
  serviceUrl: string | null | undefined
) {
  if (!serviceUrl) return false;
  return /FeatureServer(?:\/\d+)?(?:\/)?(?:\?|$)/i.test(serviceUrl);
}

function finitePoint(
  longitude: number | nullish,
  latitude: number | nullish
): [number, number] | null {
  if (longitude == null || latitude == null) return null;
  if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) return null;
  return [longitude, latitude];
}

/** Area-weighted centroid of an exterior ring, in the ring's coordinates. */
function ringCentroid(ring: number[][]): [number, number] | null {
  let twiceArea = 0;
  let x = 0;
  let y = 0;
  for (let index = 0; index < ring.length - 1; index += 1) {
    const [x0, y0] = ring[index];
    const [x1, y1] = ring[index + 1];
    if (
      x0 == null ||
      y0 == null ||
      x1 == null ||
      y1 == null ||
      !Number.isFinite(x0) ||
      !Number.isFinite(y0) ||
      !Number.isFinite(x1) ||
      !Number.isFinite(y1)
    ) {
      continue;
    }
    const cross = x0 * y1 - x1 * y0;
    twiceArea += cross;
    x += (x0 + x1) * cross;
    y += (y0 + y1) * cross;
  }
  if (twiceArea === 0) return null;
  return [x / (3 * twiceArea), y / (3 * twiceArea)];
}

/** Point as-is. A line or polygon uses its centroid, in longitude, latitude. */
export function pointFromGeometry(
  geometry: __esri.Geometry | nullish
): [number, number] | null {
  if (!geometry) return null;
  if (geometry.type === 'point') {
    const point = geometry as __esri.Point;
    return finitePoint(point.longitude ?? point.x, point.latitude ?? point.y);
  }
  if (geometry.type === 'polygon') {
    const ring = (geometry as __esri.Polygon).rings?.[0];
    const centroid = ring ? ringCentroid(ring) : null;
    if (centroid) return centroid;
  }
  if (geometry.type === 'polyline') {
    const path = (geometry as __esri.Polyline).paths?.[0];
    const middle = path?.[Math.floor((path.length - 1) / 2)];
    const midpoint = finitePoint(middle?.[0], middle?.[1]);
    if (midpoint) return midpoint;
  }
  const center = geometry.extent?.center;
  return finitePoint(
    center?.longitude ?? center?.x,
    center?.latitude ?? center?.y
  );
}

function asObjectId(value: string | number | nullish): number | null {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function featureLabel(
  feature: __esri.Graphic,
  displayField: string | nullish
): string {
  const objectId = feature.getObjectId();
  const attributes = feature.attributes ?? {};
  const named = displayField ? attributes[displayField] : undefined;
  const fallback =
    named ??
    attributes.NAME ??
    attributes.name ??
    attributes.Name ??
    (objectId != null ? `Feature ${objectId}` : 'Feature');
  return String(fallback);
}

export async function listArcgisFeatures(
  serviceUrl: string
): Promise<ArcgisFeatureChoice[]> {
  const layer = new FeatureLayer({ url: serviceUrl });
  await layer.load();
  const displayField = layer.displayField;
  const { features } = await layer.queryFeatures({
    where: '1=1',
    returnGeometry: true,
    outFields: displayField ? [displayField] : ['*'],
    outSpatialReference: WGS84_SPATIAL_REFERENCE,
    num: 100,
  });
  const choices: ArcgisFeatureChoice[] = [];
  for (const feature of features) {
    const objectId = asObjectId(feature.getObjectId());
    const point = pointFromGeometry(feature.geometry);
    if (objectId == null || !point) continue;
    choices.push({
      objectId,
      label: featureLabel(feature, displayField),
      longitude: point[0],
      latitude: point[1],
    });
  }
  return choices.sort((a, b) => a.label.localeCompare(b.label));
}

export async function queryArcgisFeaturePoints(
  serviceUrl: string,
  objectIds: number[]
): Promise<Map<number, [number, number]>> {
  const points = new Map<number, [number, number]>();
  if (objectIds.length === 0) return points;
  const layer = new FeatureLayer({ url: serviceUrl });
  const { features } = await layer.queryFeatures({
    objectIds,
    returnGeometry: true,
    outFields: ['OBJECTID'],
    outSpatialReference: WGS84_SPATIAL_REFERENCE,
  });
  for (const feature of features) {
    const objectId = asObjectId(feature.getObjectId());
    const point = pointFromGeometry(feature.geometry);
    if (objectId == null || !point) continue;
    points.set(objectId, point);
  }
  return points;
}
